import { useCallback, useEffect, useState } from "react";
import cn from "classnames";
// @ts-ignore
import union from "set.prototype.union";
// @ts-ignore
import difference from "set.prototype.difference";
import { TableColumn, TableRowType } from "./TableTypes";
import MemoizedTableRow from "./TableRow.tsx";
import TableRowSkeleton from "./TableRowSkeleton.tsx";
import TableHead from "./TableHead";
import { FilterModel } from '@hooks/useFiltersSearchParams';
import styles from "./Table.module.scss";

const setsAreEqual = (a: Set<any>, b: Set<any>) => {
  return a.size === b.size && [...a].every(item => b.has(item));
};

export type TableProps<T, TNested=T> = {
  columns: TableColumn<T>[];
  nestedColumns?: TableColumn<TNested>[];
  columnsSort?: string[];
  filters?: FilterModel[];
  data: T[];
  checkboxTooltip?: (data: T) => string;
  selectable?: boolean;
  isRowSelectable?: (data: T) => boolean;
  maxSelected?: number;
  isCheckboxDisabled?: (data: T) => boolean;
  onSelectionChange?: (selectedRowIds: string[], data: T[]) => void;
  onSortingChange?: (column: string, direction: string) => void;
  sorting?: { column: string, direction: string };
  selectedIds?: string[];
  rowId?: (row: T) => string;
  nestedRowId?: (row: TNested) => string;
  rowClassName?: string | ((row: TableRowType<T>) => string);
  nestedRowClassName?: string | ((row: TableRowType<TNested>) => string);
  rowOnClick?: (row: T) => void;
  rowTooltip?: (row: T) => string;
  className?: string;
  isHeadless?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
  idKey?: string;
  verticalAlignment?: "top" | "center";
  isSkeleton?: boolean;
  isRowCollapsible?: (data: T) => boolean;
  getNestedData?: (data: T) => TNested[];
  rowKey?: (row: T) => string;
  rowKeyNested?: (row: TNested) => string;
};

export function defaultRowId<T>(row: T, idKey?: string) {
  return String(row ? (row as Record<string, any>)[idKey || "id"] : "")
}

function Table<T, TNested=T>(props: TableProps<T, TNested>): JSX.Element {
  const [selectedRowIds, setSelectedRowIds_] = useState<Set<string>>(
    new Set<string>(),
  );
  const [selectedRowsInitialized, setSelectedRowsInitialized] = useState<boolean>(false);
  const setSelectedRowIds = (v: any) => {
    setSelectedRowsInitialized(true);
    setSelectedRowIds_(v);
  }
  const [openedRows, setOpenedRows] = useState<Set<string>>(
    new Set<string>(),
  );

  const rowId = props.rowId || defaultRowId<T>;
  const nestedRowId = props.nestedRowId || defaultRowId<TNested>;

  useEffect(() => {
    if (selectedRowsInitialized && typeof props.onSelectionChange === "function") {
      props.onSelectionChange(
        Array.from(selectedRowIds),
        props.data
      );
    }
  }, [selectedRowIds, selectedRowsInitialized]);

  useEffect(() => {
    if (props.selectedIds) {
      const newSet = new Set(props.selectedIds);
      if (!setsAreEqual(newSet, selectedRowIds)) {
        setSelectedRowIds(newSet);
      }
    }
  }, [props.selectedIds]);

  const handleToggleSelect = useCallback((rowId: string) => {
    setSelectedRowIds((currentRowIds: Set<string>) => {
      const newRowIds = new Set(currentRowIds);
      if (newRowIds.has(rowId)) {
        newRowIds.delete(rowId);
      } else {
        newRowIds.add(rowId);
      }
      return newRowIds;
    });
  }, []);

  const handleOpenRow = useCallback((rowId: string) => {
    setOpenedRows((currentRowIds: Set<string>) => {
      const newRowIds = new Set(currentRowIds);
      if (newRowIds.has(rowId)) {
        newRowIds.delete(rowId);
      } else {
        newRowIds.add(rowId);
      }
      return newRowIds;
    });
  }, []);

  const selectedRowIdsFromCurPage = props.data.map(
    (item) => rowId(item, props.idKey)
  ).filter((item) => selectedRowIds.has(item));

  const selectableData = props.data.filter((item) => (
    (typeof props.isRowSelectable !== "function")
    || props.isRowSelectable(item)
  ))
  const allPageSelected = Boolean(selectedRowIdsFromCurPage.length) && (selectableData.length === selectedRowIdsFromCurPage.length);
  const pagePartialySelected = Boolean(selectedRowIdsFromCurPage.length)
    && (selectableData.length > selectedRowIdsFromCurPage.length);

  const toggleSelectAll = useCallback(() => {
    if (props.maxSelected) {
      const curPageIdsToSelect = props.data.filter((item) => ( // доступные для выбора строи текущей строки, которые еще не выбраны
        ((typeof props.isRowSelectable !== "function")
          || props.isRowSelectable(item))
        && !selectedRowIds.has(rowId(item, props.idKey))
      )).map((item) => rowId(item, props.idKey));
      const curPageIdsSelected = new Set( // уже выбранные на данной странице
        props.data.filter((item) => (
          ((typeof props.isRowSelectable !== "function")
            || props.isRowSelectable(item))
          && selectedRowIds.has(rowId(item, props.idKey))
        )).map((item) => rowId(item, props.idKey))
      );
      if (curPageIdsSelected.size) {
        // есть выбранные на даной странице - сбрасываем их
        setSelectedRowIds(difference(selectedRowIds, curPageIdsSelected));
      } else {
        if (props.maxSelected > selectedRowIds.size) {
          // на данной странице нет выбранных строк, лимит не достигнут, выбираем строки пока не достигнем лимит
          const newSelectedIds = new Set<string>();
          let sCount = selectedRowIds.size;
          for (let idx = 0; idx < curPageIdsToSelect.length; idx++) {
            if (sCount >= props.maxSelected) break;
            newSelectedIds.add(curPageIdsToSelect[idx]);
            sCount = sCount + 1;
          }
          setSelectedRowIds(union(selectedRowIds, newSelectedIds));
        }
      }
    } else {
      const curPageIds = new Set(
        props.data.filter((item) => (
          (typeof props.isRowSelectable !== "function")
          || props.isRowSelectable(item)
        )).map((item) => rowId(item, props.idKey))
      );
      if (allPageSelected) {
        setSelectedRowIds(difference(selectedRowIds, curPageIds));
      } else {
        setSelectedRowIds(union(selectedRowIds, curPageIds));
      }
    }
  }, [selectedRowIds.size, allPageSelected, props.idKey]);

  const isSelectable = Boolean(props.selectable && (
    !props.isRowSelectable ||
    props.data.find(row => props.isRowSelectable?.(row))
  ));

  return (
    <div
      ref={props.ref}
      className={cn(
        styles.wrapper,
        props.className,
      )}
    >
      <table className={styles.table}>
        {props.isHeadless ? null : (
          <TableHead
            isSelectable={isSelectable}
            selected={pagePartialySelected ? "partial" : allPageSelected}
            filters={props.filters}
            toggleSelectAll={toggleSelectAll}
            columns={props.columns}
            onSortingChange={props.onSortingChange}
            sorting={props.sorting}
          />
        )}
        <tbody>
          {props.isSkeleton ? (
            props.data.map((_, idx) => (
              <TableRowSkeleton
                key={idx}
                columns={props.columns}
                selectable={props.selectable}
                verticalAlignment={props.verticalAlignment}
              />
            ))
          ) : (
            props.data.map((row, idx) => (
              <>
                <MemoizedTableRow<T>
                  index={idx}
                  key={props.rowKey ? props.rowKey(row) : rowId(row)}
                  selectable={props.selectable}
                  isRowSelectable={props.isRowSelectable}
                  isCheckboxDisabled={props.isCheckboxDisabled}
                  checkboxTooltip={props.checkboxTooltip}
                  row={row}
                  isSelected={selectedRowIds.has(rowId(row, props.idKey))}
                  onToggleSelect={handleToggleSelect}
                  columns={props.columns}
                  id={rowId(row, props.idKey)}
                  rowClassName={props.rowClassName}
                  rowOnClick={props.rowOnClick}
                  rowTooltip={props.rowTooltip}
                  verticalAlignment={props.verticalAlignment}
                  isCollapsible={Boolean(props.isRowCollapsible && props.isRowCollapsible(row))}
                  isOpen={openedRows.has(rowId(row, props.idKey))}
                  onToggleIsOpen={handleOpenRow}
                />
                {((props.isRowCollapsible
                  && props.isRowCollapsible(row))
                  && props.getNestedData
                  && props.nestedColumns
                  && openedRows.has(rowId(row, props.idKey))
                ) ? (
                  props.getNestedData(row).map((v, i) => (
                    <MemoizedTableRow<TNested>
                      index={i}
                      key={props.rowKeyNested ? props.rowKeyNested(v) : nestedRowId(v)}
                      isRowSelectable={() => false}
                      row={v}
                      selectable={false}
                      isSelected={false}
                      onToggleSelect={() => {}}
                      columns={props.nestedColumns ?? []}
                      id={nestedRowId(v, props.idKey)}
                      rowClassName={props.nestedRowClassName}
                      // rowOnClick={props.rowOnClick}
                      isNested
                    />
                  ))
                ) : null}
              </>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
